<template>
  <PageTemplate>

    <Loader v-if="!isLoaded"/>
    <div v-else>

      <BackButton
          :label="$t('general.button.back')"
          :to="{ name: 'deletedRecordsHome' }"
          marginBottom="5"/>

      <h6 class="element-header">{{ $t('user.title') }}</h6>

      <div class="element-box">
        <h5 class="form-header mb-4">
          {{ $t('user.deleted_list') }}
        </h5>

        <div class="row align-items-center justify-content-between">
          <PerPage :values="[10, 25, 50, 100]" @update:perPage="getUsers"/>

          <div class="form-group mb-0 col-12 col-md-4 mt-3 mt-md-0">
            <input name="searchTerm"
                   class="form-control"
                   @input="handleDebounceSearch"
                   v-model="filterDebounce.searchTerm"
                   :placeholder="$t('general.button.search')"/>
          </div>
        </div>

        <EmptyState
            v-if="!users.data?.length"
            icon="fa-trash-can"
            title="Keine gelöschten Einträge gefunden"
            subtitle="Es wurden keine gelöschten Einträge gefunden, die Ihren Suchkriterien entsprechen."/>

        <div v-else class="table-responsive overflow-auto pt-4">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>Mitarbeiternummer</th>
              <th>Status</th>
              <th>Extern/Intern</th>
              <th>Eintrittstermin</th>
              <th>Vorname</th>
              <th>Nachname</th>
              <th>Rechte</th>
              <th>Standort</th>
              <th style=" text-align: right;">
                <a href="#">{{ $t('general.actions') }}</a>
              </th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(user) in users.data" :key="user.id">
              <td>{{ user?.employeeNumber }}</td>
              <td>{{ user?.statusTranslation }}</td>
              <td>{{ user?.engagementTypeTranslation }}</td>
              <td>{{ user?.startingDate }}</td>
              <td>{{ user?.firstName }}</td>
              <td>{{ user?.lastName }}</td>
              <td>{{ user?.userAccessRightsTranslation }}</td>
              <td>
                <div
                    :class="{ 'popover-trigger': user.locations && user.locations.length > 5 }"
                    @mouseenter="handleMouseEnter($event, user)"
                    @mouseleave="user.locations && user.locations.length > 5 && hidePopover">
                  <template v-if="user.locations && user.locations.length > 5">
                    <template
                        v-for="(location, index) in user.locations.slice(0, 3)"
                        :key="location.id">
                      {{ location.name }}
                      <span v-if="index < 2">, </span>
                    </template>
                    <span
                        class="badge text-bg-primary ms-1 px-2 py-1 align-middle"
                        style="font-size: 0.75rem; font-weight: 600;">
                      +{{ user.locations.length - 3 }}
                   </span>
                  </template>
                  <template v-else-if="user.locations && user.locations.length > 0">
                    <template
                        v-for="(location, index) in user.locations"
                        :key="location.id">
                      {{ location.name }}
                      <span v-if="index < user.locations.length - 1">, </span>
                    </template>
                  </template>
                  <template v-else>
                    <span>Keine Daten</span>
                  </template>
                </div>
              </td>

              <Teleport to="body">
                <div
                    v-if="showLocationsPopover"
                    class="locations-popover"
                    :style="popoverStyle"
                    @mouseenter="keepPopover"
                    @mouseleave="hidePopover">
                  <div
                      v-for="location in activeLocations"
                      :key="location.id">
                    {{ location.name }}
                  </div>
                </div>
              </Teleport>

              <td class="text-end">
                <button class="btn btn-primary ms-2"
                        @click="undeleteModal(user?.id, user.firstName,  user.lastName)">
                  <i class="os-icon os-icon-arrow-left7"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination
            v-if="users?.pagination?.total_pages > 1"
            :totalPages="users?.pagination?.total_pages"
            :currentPage="users?.pagination?.current_page"
            :totalItems="users?.pagination?.total_items"
            :perPage="users?.pagination?.items_per_page"
            @pagechanged="getUsers"/>
      </div>
    </div>


    <ModalConfirm
        v-model="showModal"
        :confirmationQuestion=confirmModalMessage
        :header="''"
        :onConfirm="undeleteUser"/>

  </PageTemplate>
</template>


<script setup>
import deletedRecordsService from '@/api/DeletedRecords';
import CreateButton from '@/components/CreateButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import Loader from '@/components/Loader.vue';
import ModalConfirm from '@/components/modal/ModalConfirm.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import { CloseToast, ErrorToast, SuccessToast } from '@/components/VueToast';
import debounce from '@popperjs/core/lib/utils/debounce';
import { nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import BackButton from "@/components/button/BackButton.vue";

const { t }               = useI18n();
const users               = ref([]);
const confirmModalMessage = ref('');
const showModal           = ref(false);
const userId              = ref('');
const isSearching         = ref(false);
const isLoaded            = ref(false);

const filterDebounce = ref({
  searchTerm: '',
});

const debouncedSearch = debounce(async (query) => {
  await getUsers(1, undefined, filterDebounce.value);
}, 600);

const handleDebounceSearch = (event) => {
  debouncedSearch(event.target.value);
};

async function getUsers(page = 1, perPage, passedFilters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }
  const perPageRes = users.value?.pagination?.items_per_page;

  try {
    const { data } = await deletedRecordsService.getDeletedUserList({
      page: page,
      limit: perPage ?? perPageRes,
      ...(passedFilters ? passedFilters : ''),
    });

    users.value       = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

function undeleteModal(id, firstName, lastName) {
  CloseToast();
  userId.value              = '';
  confirmModalMessage.value = '';
  userId.value              = id;
  showModal.value           = true;
  confirmModalMessage.value = 'Willst du ' + firstName + ' ' + lastName + ' wiederherstellen?';
}

async function undeleteUser() {
  try {
    await deletedRecordsService.undeleteUser(userId.value);

    userId.value    = false;
    showModal.value = false;

    await getUsers();
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

const showLocationsPopover = ref(false)
const activeLocations = ref([])
const popoverStyle = ref({})

const hideTimeout = ref(null)

const showPopover = async (event, user) => {
  clearTimeout(hideTimeout.value)

  const currentTarget = event.currentTarget
  if (!currentTarget) return
  const rect = currentTarget.getBoundingClientRect()

  activeLocations.value = user.locations

  popoverStyle.value = {
    position: 'fixed',
    visibility: 'hidden',
    top: '0px',
    left: '0px',
    zIndex: 9999
  }

  showLocationsPopover.value = true

  await nextTick()

  requestAnimationFrame(() => {
    const popup = document.querySelector('.locations-popover')

    if (!popup) {
      return
    }

    const popupWidth = popup.getBoundingClientRect().width

    let left = rect.left - popupWidth - 10

    if (left < 10) {
      left = rect.right + 10
    }

    popoverStyle.value = {
      position: 'fixed',
      top: `${rect.top}px`,
      left: `${left}px`,
      zIndex: 9999,
      visibility: 'visible'
    }
  })
}

const hidePopover = () => {
  hideTimeout.value = setTimeout(() => {
    showLocationsPopover.value = false
    activeLocations.value = []
  }, 100)
}

const keepPopover = () => {
  clearTimeout(hideTimeout.value)
}

const handleMouseEnter = (event, user) => {
  if (!user.locations || user.locations.length <= 5) {
    hidePopover()
    return
  }
  showPopover(event, user)
}

onMounted(async () => {
  await Promise.all([
    getUsers(),
  ]);

  isLoaded.value = true;
});


</script>

<style scoped>
.popover-trigger {
  cursor: pointer;
}

.locations-popover {
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 10px 15px;
  min-width: 180px;
  max-width: 300px;
  max-height: 350px;
  overflow-y: auto;
  font-size: 14px;
}

.locations-popover div {
  white-space: normal;
  word-break: break-word;
}
</style>